import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Play, 
  Pause,
  Lock, 
  FileText, 
  Headphones, 
  ArrowLeft,
  Volume2,
  X
} from "lucide-react";
import { toast } from "sonner";

export default function BrowsePage() {
  const navigate = useNavigate();
  const { category } = useParams();
  const { user } = useAuth();
  const [content, setContent] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(category || "1L");
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [playingItem, setPlayingItem] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const audioRef = useRef(null);
  // Public 1-minute preview state (separate from full playback)
  const previewAudioRef = useRef(null);
  const [previewingId, setPreviewingId] = useState(null);

  const handlePreview = (item) => {
    if (previewingId === item.content_id) {
      if (previewAudioRef.current) previewAudioRef.current.pause();
      setPreviewingId(null);
      return;
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    const a = new Audio(`${API}/packs/preview/${item.content_id}`);
    a.onended = () => setPreviewingId(null);
    a.onerror = () => {
      toast.error("Preview unavailable for this song");
      setPreviewingId(null);
    };
    previewAudioRef.current = a;
    setPreviewingId(item.content_id);
    a.play().catch(() => setPreviewingId(null));
  };

  const categories = [
    { id: "1L", name: "1L Courses" },
    { id: "2L", name: "2L Courses" },
    { id: "3L", name: "3L Courses" },
  ];

  useEffect(() => {
    fetchData();
  }, [user]);

  useEffect(() => {
    if (category) {
      setSelectedCategory(category);
    }
  }, [category]);

  const fetchData = async () => {
    try {
      const [contentRes, coursesRes] = await Promise.all([
        axios.get(`${API}/content`),
        axios.get(`${API}/courses-v2`)
      ]);
      setContent(contentRes.data);
      setCourses(coursesRes.data);
      
      if (user) {
        const statusRes = await axios.get(`${API}/subscription/status`);
        setSubscriptionStatus(statusRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch content", error);
    } finally {
      setLoading(false);
    }
  };

  const hasSubscription = subscriptionStatus?.has_subscription;

  const filteredCourses = courses.filter(c => c.category === selectedCategory);

  const getContentByCourse = (courseName) => {
    return content.filter(c => c.course_name === courseName && c.category === selectedCategory);
  };

  const handlePlay = async (item) => {
    if (item.locked && !item.is_preview) {
      toast.info("Subscribe to access this content");
      navigate("/subscribe");
      return;
    }

    if (playingItem?.content_id === item.content_id) {
      if (audioRef.current?.paused) {
        audioRef.current.play();
      } else {
        audioRef.current?.pause();
      }
      return;
    }

    try {
      const response = await axios.get(`${API}/content/stream/${item.content_id}`);
      setPlayingItem({ ...item, streamUrl: response.data.stream_url });
    } catch (error) {
      if (error.response?.status === 403) {
        toast.info("Subscribe to access this content");
        navigate("/subscribe");
      } else {
        toast.error("Failed to play content");
      }
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    return `${mins} min`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gold animate-pulse font-serif text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="browse-page">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            className="mb-4 text-muted-foreground hover:text-foreground"
            onClick={() => navigate("/")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <h1 className="font-serif text-3xl font-bold mb-2">Preview Our Content</h1>
          <p className="text-muted-foreground">
            Listen to a 1-minute preview of any song. Subscribe or buy a pack for full access.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {categories.map(cat => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? "default" : "outline"}
              className={selectedCategory === cat.id ? "bg-gold text-primary-foreground" : ""}
              onClick={() => setSelectedCategory(cat.id)}
              data-testid={`category-tab-${cat.id}`}
            >
              {cat.name}
            </Button>
          ))}
        </div>

        {/* Subscribe Banner */}
        {!hasSubscription && (
          <Card className="mb-8 bg-gold/10 border-gold/30">
            <CardContent className="py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-serif text-lg font-semibold mb-1">Unlock All Content</h3>
                <p className="text-sm text-muted-foreground">
                  Get unlimited access to 60+ topics. Subscribe now.
                </p>
              </div>
              <Button 
                className="bg-gold text-primary-foreground hover:bg-gold/90"
                onClick={() => navigate("/subscribe")}
                data-testid="browse-subscribe-cta"
              >
                Subscribe Now
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Courses */}
        <div className="space-y-8">
          {filteredCourses.map(course => {
            const courseContent = getContentByCourse(course.course_name);
            if (courseContent.length === 0) return null;

            return (
              <div key={course.course_name} data-testid={`course-section-${course.course_name.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h2 className="font-serif text-xl font-bold">{course.course_name}</h2>
                    <Badge variant="outline">
                      {course.topics_count} topics
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {courseContent.map(item => (
                    <Card 
                      key={item.content_id}
                      className="group hover:border-gold/50 transition-all"
                      data-testid={`content-item-${item.content_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="relative w-12 h-12 bg-card rounded-lg flex items-center justify-center flex-shrink-0">
                            {item.content_type === "audio" ? (
                              <Headphones className="h-5 w-5 text-gold" />
                            ) : (
                              <FileText className="h-5 w-5 text-gold" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <h3 className="font-medium text-sm line-clamp-1 group-hover:text-gold transition-colors">
                                {item.title}
                              </h3>
                            </div>
                            {item.song_title && (
                              <p className="text-xs italic text-primary line-clamp-1 mb-1" data-testid={`song-title-${item.content_id}`}>
                                {item.song_title}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                              {item.description}
                            </p>
                            
                            <div className="flex items-center justify-between">
                              {item.content_type === "audio" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 border-gold text-gold hover:bg-gold hover:text-primary-foreground"
                                  onClick={() => handlePreview(item)}
                                  data-testid={`preview-${item.content_id}`}
                                >
                                  {previewingId === item.content_id ? (
                                    <><Pause className="h-3 w-3 mr-1" /> Stop</>
                                  ) : (
                                    <><Play className="h-3 w-3 mr-1" /> Preview</>
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Audio Player */}
      {playingItem && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4 z-50" data-testid="audio-player">
          <div className="container mx-auto flex items-center gap-4">
            <div className="w-10 h-10 bg-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Headphones className="h-5 w-5 text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm line-clamp-1">{playingItem.title}</h4>
              <p className="text-xs text-muted-foreground">{playingItem.course_name}</p>
              {/* Progress bar */}
              <div className="mt-2 h-1 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gold transition-all duration-200"
                  style={{ width: `${currentProgress}%` }}
                />
              </div>
              {!hasSubscription && (
                <p className="text-xs text-amber-500 mt-1">40-second preview</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-10 w-10 rounded-full"
              onClick={() => {
                if (audioRef.current) {
                  if (isPlaying) {
                    audioRef.current.pause();
                  } else {
                    audioRef.current.play();
                  }
                }
              }}
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                audioRef.current?.pause();
                setPlayingItem(null);
                setIsPlaying(false);
                setCurrentProgress(0);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
            <audio
              ref={audioRef}
              src={playingItem.streamUrl}
              autoPlay
              className="hidden"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onTimeUpdate={() => {
                if (audioRef.current && audioRef.current.duration) {
                  const currentTime = audioRef.current.currentTime;
                  const progress = (currentTime / audioRef.current.duration) * 100;
                  setCurrentProgress(progress);
                  
                  // Fade out for non-subscribers (30-40 seconds)
                  if (!hasSubscription) {
                    if (currentTime >= 30 && currentTime < 40) {
                      // Gradually reduce volume from 1 to 0 over 10 seconds
                      const fadeProgress = (currentTime - 30) / 10;
                      audioRef.current.volume = Math.max(0, 1 - fadeProgress);
                    } else if (currentTime >= 40) {
                      // Stop playback at 40 seconds
                      audioRef.current.pause();
                      audioRef.current.currentTime = 0;
                      audioRef.current.volume = 1; // Reset volume for next play
                      setCurrentProgress(0);
                      setIsPlaying(false);
                      toast.info("Preview ended. Subscribe for full access!", {
                        action: {
                          label: "Subscribe",
                          onClick: () => navigate("/subscribe")
                        }
                      });
                    } else {
                      // Ensure full volume before 30 seconds
                      audioRef.current.volume = 1;
                    }
                  }
                }
              }}
              onEnded={() => {
                setPlayingItem(null);
                setIsPlaying(false);
                setCurrentProgress(0);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
